import clsx from 'clsx'
import cloneDeep from 'lodash/cloneDeep'
import React from 'react'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import { useLocale } from '../../../hooks/use-locale'
import { CurrencyEnum } from '../../../lib/graphql'
import { percentage, roundedPrice } from '../../../utils'

interface ServicesFeesBarProps {
  servicesFeesPercentage: number
  totalSpentCount: { total: number; currency: CurrencyEnum }[]
  milestones: { requiredSpentAmount: number; userFeesRate: number }[]
}

export const ServicesFeesBar = (props: ServicesFeesBarProps) => {
  const intl = useIntl()
  const { locale } = useLocale()
  const sumCurrencies = props.totalSpentCount.reduce(
    (acc, { total }) => acc + total,
    0,
  )

  const propsMilesstones = cloneDeep(props.milestones)

  let barMaxAmountCount = 0
  if (propsMilesstones.length === 0 || propsMilesstones.length === 1) {
    // If no milestone
    barMaxAmountCount = sumCurrencies * 3
  } else if (
    sumCurrencies >
    propsMilesstones[propsMilesstones.length - 1].requiredSpentAmount
  ) {
    // If the sum paid reached the last milestone we increase the last placeholder milestone to the sum value * 2
    barMaxAmountCount = sumCurrencies * 2
  } else {
    // If we did not reach the last milestone we just add 30% of the last milestone
    barMaxAmountCount =
      propsMilesstones[propsMilesstones.length - 1].requiredSpentAmount * 1.3
  }

  // Create a placeholder milestone to add the infinit milestone at the end
  propsMilesstones.push({
    requiredSpentAmount: barMaxAmountCount,
    userFeesRate: 0,
  })

  const milestones: {
    fillPerc: number
    width: number
    milestone: { requiredSpentAmount: number; userFeesRate: number }
  }[] = []

  const lastMilestoneAmount = barMaxAmountCount

  for (let i = 0; i < propsMilesstones.length; i++) {
    const milestone = propsMilesstones[i]
    const prevMilestone: { requiredSpentAmount: number; userFeesRate: number } =
      i === 0
        ? { requiredSpentAmount: 0, userFeesRate: 0 }
        : propsMilesstones[i - 1]

    let fillPerc = 0
    if (
      sumCurrencies > prevMilestone.requiredSpentAmount &&
      sumCurrencies <= milestone.requiredSpentAmount
    ) {
      // We subtract the milestones amount to get the accurant % of fill base on only the milestone
      const startAmount = sumCurrencies - prevMilestone.requiredSpentAmount
      const endAmount =
        milestone.requiredSpentAmount - prevMilestone.requiredSpentAmount
      fillPerc = parseFloat(((startAmount * 100) / endAmount).toFixed(2))
    } else if (sumCurrencies > milestone.requiredSpentAmount) {
      fillPerc = 100
    } else {
      fillPerc = 0
    }

    let width = 0
    if (i + 1 < propsMilesstones.length) {
      // We subtract the milestones amount to get the accurant % of width base on only the milestone
      const startAmount =
        milestone.requiredSpentAmount - prevMilestone.requiredSpentAmount
      const endAmount = lastMilestoneAmount - prevMilestone.requiredSpentAmount
      width =
        endAmount === 0
          ? 100
          : parseFloat(((startAmount * 100) / endAmount).toFixed(2))
    } else {
      const totalWidth = milestones.reduce((acc, { width }) => acc + width, 0)
      width = parseFloat((100 - totalWidth).toFixed(2))
    }

    milestones.push({ fillPerc, width, milestone })
  }

  return (
    <div className={s.bar}>
      {milestones.map(({ fillPerc, width, milestone }, index) => {
        if (index === 0) {
          return (
            <React.Fragment key={index}>
              <div className={clsx(s.milestone, s.firstMilestone)}>
                <p>
                  {roundedPrice(milestone.requiredSpentAmount, {
                    isCompact: true,
                  })}{' '}
                  (
                  {intl.formatMessage({
                    id: 'ui.Contracts.spentAmount',
                    defaultMessage: 'dépenses',
                  })}
                  )
                </p>
                <div className={s.verticalBar} />
                <p>
                  {percentage({ rate: milestone.userFeesRate, locale })} (
                  {intl.formatMessage({
                    id: 'ui.Contracts.serviceFees',
                    defaultMessage: 'Frais de services',
                  })}
                  )
                </p>
              </div>
              <div style={{ width: `${width}%` }} className={s.barPart}>
                <div className={s.barFill} style={{ width: `${fillPerc}%` }} />
              </div>
            </React.Fragment>
          )
        }
        return (
          <React.Fragment key={index}>
            <div
              style={{
                width: `${width}%`,
                minWidth: index === 1 ? '210px' : '40px',
              }}
              className={s.barPart}
            >
              <div className={s.barFill} style={{ width: `${fillPerc}%` }} />
            </div>
            {index !== milestones.length - 1 && (
              <div className={clsx(s.milestone)}>
                <p>
                  {roundedPrice(milestone.requiredSpentAmount, {
                    isCompact: true,
                  })}
                </p>
                <div className={s.verticalBar} />
                <p>{percentage({ rate: milestone.userFeesRate, locale })}</p>
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}
